import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signUp, signIn } from '../../firebase/auth';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { sendCustomPasswordResetEmail, sendCustomVerificationEmail } from '../../firebase/authEmails';
import Navbar from '../Navbar';
import SiteFooter from '../SiteFooter';

const PRIMARY_BUTTON_CLASS = 'w-full py-4 px-6 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-bold hover:shadow-lg transition disabled:opacity-50';

const LoginPage = () => {
  const [mode, setMode] = useState('login');
  const [view, setView] = useState('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const verificationHoldRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        if (verificationHoldRef.current && !user.emailVerified) {
          setCheckingAuth(false);
          setView('verify');
          setVerificationEmail(user.email || '');
          return;
        }

        navigate(from, { replace: true });
      } else {
        setCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [from, navigate]);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const resetMessages = () => {
    setError('');
    setInfoMessage('');
  };

  const switchToAuthView = (nextMode = 'login') => {
    resetMessages();
    setView('auth');
    setMode(nextMode);
    setPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (!isValidEmail(email)) {
        throw new Error('Voer een geldig e-mailadres in');
      }

      if (password.length < 10) {
        throw new Error('Wachtwoord moet minimaal 10 tekens zijn');
      }

      if (mode === 'signup') {
        if (password !== confirmPassword) {
          throw new Error('Wachtwoorden komen niet overeen');
        }
        if (!acceptTerms) {
          throw new Error('Je moet akkoord gaan met de voorwaarden');
        }
        if (!displayName.trim()) {
          throw new Error('Voer je naam in');
        }

        verificationHoldRef.current = true;
        const user = await signUp(email, password, displayName.trim());
        setVerificationEmail(user.email || email);
        setView('verify');
        setPassword('');
        setConfirmPassword('');
        try {
          await sendCustomVerificationEmail(from);
          setInfoMessage('We hebben een verificatie-e-mail gestuurd. Check ook je spam.');
        } catch (emailError) {
          setError(emailError.message || 'Je account is aangemaakt, maar we konden de verificatie-e-mail niet versturen.');
        }
      } else {
        await signIn(email, password);
        navigate(from, { replace: true });
      }
    } catch (submitError) {
      verificationHoldRef.current = false;
      setError(submitError.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (!isValidEmail(resetEmail)) {
        throw new Error('Voer een geldig e-mailadres in');
      }

      const result = await sendCustomPasswordResetEmail(resetEmail, '/login');
      setInfoMessage(
        result?.message || 'We hebben een e-mail gestuurd waarmee je je wachtwoord kunt resetten. Check ook je spam.',
      );
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    resetMessages();
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Je sessie is verlopen. Log opnieuw in om een verificatie-e-mail te ontvangen.');
      }

      const result = await sendCustomVerificationEmail(from);
      setInfoMessage(
        result?.alreadyVerified
          ? 'Je e-mailadres is al geverifieerd.'
          : 'We hebben de verificatie-e-mail opnieuw verstuurd. Check ook je spam.',
      );
    } catch (verificationError) {
      setError(verificationError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCheck = async () => {
    resetMessages();
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Je sessie is verlopen. Log opnieuw in en probeer het opnieuw.');
      }

      await user.reload();

      if (auth.currentUser?.emailVerified) {
        verificationHoldRef.current = false;
        navigate(from, { replace: true });
      } else {
        setError('Je e-mail is nog niet geverifieerd. Check je inbox en spam.');
      }
    } catch (verificationError) {
      setError(verificationError.message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Laden...</p>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex flex-col">
      <Navbar />

      <div className="flex-1">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              {view === 'reset'
                ? 'Wachtwoord resetten'
                : view === 'verify'
                  ? 'Check je e-mail'
                  : mode === 'signup'
                    ? 'Maak een account aan'
                    : 'Log in op je account'}
            </h1>
            <p className="text-lg text-gray-600">
              {view === 'reset'
                ? 'Vul je e-mailadres in en we sturen je een resetlink.'
                : view === 'verify'
                  ? `We hebben een verificatie-e-mail gestuurd naar ${verificationEmail || email}.`
                  : mode === 'signup'
                    ? 'Start met kopen en verkopen van cadeaubonnen'
                    : 'Welkom terug bij Swapify'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            {view === 'verify' ? (
              <div>
                <div className="rounded-xl border border-orange-100 bg-orange-50 p-5 text-sm text-gray-700">
                  Klik op de link in de e-mail om je account te activeren.
                </div>

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {infoMessage && (
                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                    {infoMessage}
                  </div>
                )}

                <div className="mt-6 space-y-3">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={loading}
                    className="w-full py-4 px-6 border border-orange-200 text-orange-700 bg-white rounded-xl font-semibold hover:bg-orange-50 transition disabled:opacity-50"
                  >
                    Verificatie-e-mail opnieuw versturen
                  </button>
                  <button
                    type="button"
                    onClick={handleVerificationCheck}
                    disabled={loading}
                    className={PRIMARY_BUTTON_CLASS}
                  >
                    {loading ? 'Bezig...' : 'Ik heb mijn e-mail geverifieerd'}
                  </button>
                </div>
              </div>
            ) : view === 'reset' ? (
              <form onSubmit={handlePasswordReset}>
                <div className="mb-4">
                  <label className="block text-gray-900 font-semibold mb-2">
                    E-mailadres
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="jouw@email.nl"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                    required
                  />
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {infoMessage && (
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                    {infoMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={PRIMARY_BUTTON_CLASS}
                >
                  {loading ? 'Bezig...' : 'Stuur resetlink'}
                </button>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-center text-sm text-gray-600">
                    <button
                      type="button"
                      onClick={() => {
                        setResetEmail(email);
                        switchToAuthView('login');
                      }}
                      className="text-red-500 hover:underline font-semibold"
                    >
                      Terug naar inloggen
                    </button>
                  </p>
                </div>
              </form>
            ) : (
              <>
                <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                  <button
                    onClick={() => switchToAuthView('login')}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      mode === 'login' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'
                    }`}
                  >
                    Inloggen
                  </button>
                  <button
                    onClick={() => switchToAuthView('signup')}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      mode === 'signup' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'
                    }`}
                  >
                    Registreren
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {mode === 'signup' && (
                    <div className="mb-4">
                      <label className="block text-gray-900 font-semibold mb-2">
                        Volledige naam
                      </label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Voor- en achternaam"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                        required
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-gray-900 font-semibold mb-2">
                      E-mailadres
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jouw@email.nl"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-gray-900 font-semibold">
                        Wachtwoord
                      </label>
                      {mode === 'login' && (
                        <button
                          type="button"
                          onClick={() => {
                            resetMessages();
                            setResetEmail(email);
                            setView('reset');
                          }}
                          className="text-sm text-red-500 hover:underline"
                        >
                          Wachtwoord vergeten?
                        </button>
                      )}
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimaal 10 tekens"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                      required
                    />
                  </div>

                  {mode === 'signup' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-gray-900 font-semibold mb-2">
                          Bevestig wachtwoord
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Herhaal je wachtwoord"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                          required
                        />
                      </div>

                      <div className="mb-6">
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            className="w-5 h-5 text-red-500 rounded mt-1 focus:ring-red-500"
                          />
                          <span className="text-sm text-gray-700">
                            Ik ga akkoord met de{' '}
                            <Link to="/terms" className="text-red-500 hover:underline">
                              algemene voorwaarden
                            </Link>{' '}
                            en het{' '}
                            <Link to="/privacy" className="text-red-500 hover:underline">
                              privacy beleid
                            </Link>
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={PRIMARY_BUTTON_CLASS}
                  >
                    {loading ? 'Bezig...' : (mode === 'login' ? 'Inloggen' : 'Account aanmaken')}
                  </button>
                </form>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-center text-sm text-gray-600">
                    {mode === 'login' ? 'Nog geen account?' : 'Al een account?'}{' '}
                    <button
                      onClick={() => switchToAuthView(mode === 'login' ? 'signup' : 'login')}
                      className="text-red-500 hover:underline font-semibold"
                    >
                      {mode === 'login' ? 'Registreer hier' : 'Log in'}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default LoginPage;
